<script setup>
import { ref, onMounted, computed } from 'vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import FileUploadWidget from '@/presentation/components/input/FileUploadWidget.vue'
import { EVENTS } from '@/shared/constants/commons'
import Events from '@/events/events'
import Commons from '@/shared/utils/commons'

const props = defineProps({
  restaurantId: { type: String, required: true },
  currentDrinks: { type: Array, default: () => [] },
  drinkToEdit: { type: Object, default: null }
})

const emit = defineEmits(['close', 'success'])

const isEditing = computed(() => !!props.drinkToEdit)
const saveTriggerRef = ref(null)
const isLoading = ref(false)

const defaultForm = () => ({
  id: '',
  name: '',
  amount: 0,
  max: 1,
  image: ''
})

const form = ref(defaultForm())

onMounted(() => {
  if (props.drinkToEdit) {
    form.value = { ...props.drinkToEdit }
  }
})

const handleImageSaved = (data) => {
  if (data?.files?.length > 0) {
    form.value.image = data.files[0].url
  }
}

const submit = () => {
  if (!form.value.name?.trim()) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Validation', body: 'Le nom est requis', type: 'error' })
    return
  }
  if (!form.value.amount) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Validation', body: 'Le prix est requis', type: 'error' })
    return
  }

  isLoading.value = true
  
  // Clone current drinks and update/add the current one
  const updatedDrinks = [...props.currentDrinks]
  if (isEditing.value) {
    const idx = updatedDrinks.findIndex(d => d.id === form.value.id)
    if (idx !== -1) updatedDrinks[idx] = { ...form.value }
  } else {
    updatedDrinks.push({
      ...form.value,
      id: 'drink_' + Date.now()
    })
  }

  const payload = { 
    restaurantId: props.restaurantId,
    drinks: updatedDrinks
  }
  
  saveTriggerRef.value?.trigger(payload)
}

const onSuccess = () => {
  isLoading.value = false
  emit('success')
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Succès', body: 'Boisson enregistrée avec succès.', type: 'success' })
}

const onError = (err) => {
  isLoading.value = false
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Erreur', body: err?.message || 'Erreur lors de l\'enregistrement', type: 'error' })
}
</script>

<template>
  <div class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" @click.self="$emit('close')">
    <div class="bg-white w-full max-w-lg rounded-2xl shadow-xl overflow-hidden flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
        <div>
          <h2 class="text-lg font-bold text-gray-900">{{ isEditing ? 'Modifier la boisson' : 'Ajouter une boisson' }}</h2>
          <p class="text-xs text-gray-500">Configurez les détails de la boisson</p>
        </div>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100">
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <!-- Content -->
      <div class="p-6 space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Image de la boisson</label>
          <FileUploadWidget
            :defaultUrls="form.image ? [form.image] : []"
            :maxFiles="1"
            :acceptedFiles="['image/jpeg', 'image/png', 'image/webp']"
            @saved="handleImageSaved"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Nom de la boisson <span class="text-rose-500">*</span></label>
          <input v-model="form.name" type="text" placeholder="Ex: Coca-Cola 33cl" class="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none" />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Prix (FCFA) <span class="text-rose-500">*</span></label>
            <input v-model.number="form.amount" type="number" class="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Max / commande</label>
            <input v-model.number="form.max" type="number" min="1" class="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none" />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50/50">
        <button @click="$emit('close')" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50">
          Annuler
        </button>
        <button @click="submit" :disabled="isLoading" class="px-8 py-2 text-sm font-medium text-white bg-orange-600 rounded-xl hover:bg-orange-700 shadow-sm disabled:opacity-60 transition-all flex items-center gap-2">
          <span v-if="isLoading" class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
          {{ isEditing ? 'Mettre à jour' : 'Enregistrer' }}
        </button>
      </div>
    </div>

    <!-- Mutation Trigger -->
    <ActionTrigger
      dataKey="updateStorePreferences"
      ref="saveTriggerRef"
      @success="onSuccess"
      @failed="onError"
    />
  </div>
</template>
